import React from "react";
import type { CarsCompletedThroughputRow } from "../../../types";
import {
  addDaysToYmd,
  formatDateForDisplay,
  formatDateShort,
  formatMonthYearLong,
} from "../../../utils/dateUtils";
import { Card, ErrorState, Icons, Loading } from "../../shared";

type PeriodKind = "day" | "week" | "month";

const EFFORT_META = [
  {
    key: "leve" as const,
    label: "Leve",
    bar: "border-l-emerald-500 dark:!border-l-emerald-400",
    text: "text-emerald-800 dark:text-emerald-300",
    card: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:border-emerald-700",
  },
  {
    key: "medio" as const,
    label: "Médio",
    bar: "border-l-amber-400 dark:!border-l-amber-400",
    text: "text-amber-900 dark:text-amber-300",
    card: "bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-700",
  },
  {
    key: "pesado" as const,
    label: "Pesado",
    bar: "border-l-red-500 dark:!border-l-rose-400",
    text: "text-red-900 dark:text-rose-300",
    card: "bg-red-50 border-red-200 dark:bg-rose-950 dark:border-rose-700",
  },
];

function formatPeriodLabel(period: string, kind: PeriodKind): string {
  if (kind === "month") {
    const [year, month] = period.split("-");
    if (year && month) {
      return formatMonthYearLong(parseInt(month, 10), parseInt(year, 10));
    }
    return period;
  }

  if (kind === "week") {
    const weekEnd = addDaysToYmd(period, 6);
    return `${formatDateShort(period)} — ${formatDateShort(weekEnd)}`;
  }

  return formatDateForDisplay(period);
}

function formatPeriodLabelShort(period: string, kind: PeriodKind): string {
  if (kind === "month") {
    const [year, month] = period.split("-");
    if (year && month) {
      return formatMonthYearLong(parseInt(month, 10), parseInt(year, 10));
    }
    return period;
  }

  if (kind === "week") {
    return `Sem. ${formatDateShort(period)}`;
  }

  return formatDateShort(period);
}

export interface CarsCompletedThroughputPanelProps {
  title: string;
  subtitle: string;
  rangeLabel: string;
  rows: CarsCompletedThroughputRow[];
  periodKind: PeriodKind;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  onPeriodClick?: (period: string, periodKind: PeriodKind) => void;
}

export const CarsCompletedThroughputPanel: React.FC<
  CarsCompletedThroughputPanelProps
> = ({
  title,
  subtitle,
  rangeLabel,
  rows,
  periodKind,
  loading,
  error,
  onRetry,
  onPeriodClick,
}) => {
  const totalCars = rows.reduce((sum, row) => sum + row.total, 0);
  const showEmpty = !loading && !error && rows.length === 0;

  return (
    <Card className="p-4 sm:p-6 border border-gray-100 shadow-sm h-full">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
        <p className="text-xs font-medium text-sky-700 dark:text-sky-400 mt-1">{rangeLabel}</p>
      </div>

      {loading && (
        <Loading message="Carregando produção…" size="md" className="py-8" />
      )}

      {!loading && error && (
        <ErrorState
          message={error}
          variant="inline"
          onRetry={onRetry}
          className="py-4"
        />
      )}

      {showEmpty && (
        <div
          className="flex flex-col items-center justify-center py-10 px-4 text-center rounded-xl bg-gray-50 dark:bg-slate-900/60 border border-dashed border-gray-200 dark:border-slate-600"
          role="status"
        >
          <Icons.Car sx={{ fontSize: 44 }} className="text-gray-300 dark:text-slate-600" />
          <h3 className="mt-3 text-base font-semibold text-gray-800 dark:text-slate-100">
            Nenhum veículo concluído
          </h3>
          <p className="mt-1.5 text-sm text-gray-500 dark:text-slate-400 max-w-[16rem] leading-snug">
            Não há veículos finalizados neste período.
          </p>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 px-4 py-3">
            <Icons.Car sx={{ fontSize: 24 }} className="text-slate-600 dark:text-slate-300" />
            <div>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">Total no período</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums">{totalCars}</p>
            </div>
          </div>

          <ul className="divide-y divide-gray-100 dark:divide-slate-700 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
            {rows.map((row) => (
              <li key={row.period}>
                <div
                  className={`px-3 py-3 sm:px-4 transition-colors ${
                    onPeriodClick
                      ? "hover:bg-sky-50 dark:hover:bg-slate-700/50 cursor-pointer"
                      : "hover:bg-sky-50/70 dark:hover:bg-slate-700/40"
                  }`}
                  role={onPeriodClick ? "button" : undefined}
                  tabIndex={onPeriodClick ? 0 : undefined}
                  onClick={
                    onPeriodClick
                      ? () => onPeriodClick(row.period, periodKind)
                      : undefined
                  }
                  onKeyDown={
                    onPeriodClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onPeriodClick(row.period, periodKind);
                          }
                        }
                      : undefined
                  }
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <p
                      className="text-sm font-semibold text-gray-900 dark:text-white"
                      title={formatPeriodLabel(row.period, periodKind)}
                    >
                      {formatPeriodLabelShort(row.period, periodKind)}
                      {onPeriodClick ? (
                        <span className="ml-1.5 text-xs font-normal text-sky-600 dark:text-sky-400">
                          Ver detalhes →
                        </span>
                      ) : null}
                    </p>
                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100 tabular-nums">
                      {row.total} {row.total === 1 ? "veículo" : "veículos"}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {EFFORT_META.map(({ key, label, bar, text, card }) => (
                      <div
                        key={key}
                        className={`rounded-lg border px-2 py-2 border-l-[3px] ${bar} ${card}`}
                      >
                        <p className={`text-[10px] font-bold uppercase tracking-wide ${text}`}>
                          {label}
                        </p>
                        <p className="mt-1 text-base font-bold tabular-nums text-gray-900 dark:text-white">
                          {row.byEffort[key]}
                        </p>
                      </div>
                    ))}
                  </div>
                  {(row.byService ?? []).length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {row.byService!.map((service) => (
                        <li
                          key={service.serviceId}
                          className="flex items-center justify-between gap-2 rounded-md bg-white dark:bg-slate-900/70 border border-gray-200 dark:border-slate-600 px-2 py-1"
                        >
                          <span className="text-xs text-gray-700 dark:text-slate-300 truncate" title={service.serviceName}>
                            {service.serviceName}
                          </span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums shrink-0">
                            {service.total}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
};
